import { useManager } from 'dreamstate';
import { useCallback, useState, ReactNode, PropsWithChildren } from 'react';

import { rem, Button, FormControl, FormLabel, Switch, SystemStyleObject, Flex } from '@vimeo/bokeh';
import { ReviewQuestion } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Logger } from '@vimeo/logging';
import { useLiveStreamBroadcasterTracking } from '@vimeo/picox';

import { IQnAContext, QnAManager } from '../../../../store/firebase/interaction/qna';
import { trackClickStartQna } from '../../../../tracking/tracking_engagement_qna';
import {
  T_ALLOW_ANONYMOUS_QUESTIONS,
  T_CHOOSE_QUESTIONS_MESSAGE,
  T_GIVE_VIEWERS_OPTION_TO_HIDE_THEIR_NAME,
  T_MODERATE_QUESTIONS,
  T_START_QNA,
  T_START_QNA_MESSAGE,
} from '../../../../translations';
import { createDomName } from '../../../../utils/dom/identifying';
import { CircleTip } from '../../../circle_tip';
import { EmptyStatePlaceholder } from '../../../panels/empty';

const FORM_CONTROL_STYLE: SystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  lineHeight: rem(24),
  padding: `${rem(4)} 0`,
};

const FORM_LABEL_STYLE: SystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  color: 'text-primary',
  fontSize: rem(12),
  columnGap: rem(4),
  marginBottom: 0,
};

interface ISessionInactiveProps extends IBaseDomElementProps {
  qnaContext?: IQnAContext;
  flexGrow?: number;
  height?: string;
}

export function SessionInactive({
  id = createDomName('qna-session-inactive'),
  className = createDomName('qna-session-inactive'),
  qnaContext: {
    activeSessionId,
    isEventModerated,
    isAnonymousQuestionsDisabled,
    config: { canUseQnaModeration },
    qnaActions: {
      toggleModerationState,
      toggleAnonymousQuestionsState,
      createQnASession,
      openQnASession,
    },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(QnAManager, ({ isEventModerated, isAnonymousQuestionsDisabled, config }) => [
    isEventModerated,
    isAnonymousQuestionsDisabled,
    config,
  ]),
  children = null as ReactNode,
  flexGrow = 1,
  height = '100%',
}: PropsWithChildren<ISessionInactiveProps>) {
  const { trackLiveStreamQaStarted } = useLiveStreamBroadcasterTracking();
  const [isSessionLoading, setIsSessionLoading] = useState(false);

  const onToggleModerationState = useCallback(
    () => toggleModerationState(!isEventModerated),
    [isEventModerated, toggleModerationState],
  );

  const onToggleAnonymousQuestionsState = useCallback(
    () => toggleAnonymousQuestionsState(!isAnonymousQuestionsDisabled),
    [isAnonymousQuestionsDisabled, toggleAnonymousQuestionsState],
  );

  const onStartSessionClick = useCallback(async () => {
    // Still loading data.
    if (isEventModerated === null) {
      return;
    }

    try {
      setIsSessionLoading(true);

      await openQnASession(await createQnASession(isEventModerated));
      trackLiveStreamQaStarted();
    } catch (error) {
      setIsSessionLoading(false);
      Logger.getGlobal().error('Failed to start qna session:', error);
    } finally {
      setIsSessionLoading(false);
      trackClickStartQna(isEventModerated);
    }
  }, [isEventModerated, createQnASession, openQnASession, trackLiveStreamQaStarted]);

  return (
    <EmptyStatePlaceholder
      id={id}
      className={className}
      isWithScroll={false}
      icon={<ReviewQuestion boxSize={'lg'} />}
      description={T_START_QNA_MESSAGE}
      flexGrow={flexGrow}
      height={height}
      control={
        <Flex
          alignItems={'center'}
          justifyContent={'center'}
          flexDirection={'column'}
          width={'100%'}
          maxWidth={rem(360)}
        >
          {canUseQnaModeration ? (
            <>
              <FormControl sx={FORM_CONTROL_STYLE}>
                <FormLabel htmlFor={createDomName(id, 'moderation-toggle')} sx={FORM_LABEL_STYLE}>
                  {T_MODERATE_QUESTIONS}
                  <CircleTip label={T_CHOOSE_QUESTIONS_MESSAGE} />
                </FormLabel>

                <Switch
                  id={createDomName(id, 'moderation-toggle')}
                  className={createDomName(className, 'moderation-toggle')}
                  size={'sm'}
                  isChecked={Boolean(isEventModerated)}
                  onChange={onToggleModerationState}
                />
              </FormControl>

              <FormControl sx={FORM_CONTROL_STYLE}>
                <FormLabel
                  htmlFor={createDomName(id, 'anonymous-question-toggle')}
                  sx={FORM_LABEL_STYLE}
                >
                  {T_ALLOW_ANONYMOUS_QUESTIONS}
                  <CircleTip label={T_GIVE_VIEWERS_OPTION_TO_HIDE_THEIR_NAME} />
                </FormLabel>

                <Switch
                  id={createDomName(id, 'anonymous-question-toggle')}
                  className={createDomName(className, 'anonymous-question-toggle')}
                  size={'sm'}
                  isChecked={!Boolean(isAnonymousQuestionsDisabled)}
                  onChange={onToggleAnonymousQuestionsState}
                />
              </FormControl>
            </>
          ) : null}

          <Button
            id={createDomName(id, 'session-control-button')}
            className={createDomName(className, 'session-control-button')}
            variant={'primary'}
            size={'sm'}
            marginTop={rem(16)}
            padding={`0 ${rem(12)}`}
            isDisabled={Boolean(activeSessionId || isSessionLoading)}
            onClick={onStartSessionClick}
          >
            {T_START_QNA}
          </Button>

          {children}
        </Flex>
      }
    />
  );
}
